import {
  getConversionPrompt,
  isUpgradeReason,
  isUpgradeReturnAction,
} from './conversionPrompts';

const STORAGE_KEY = 'affinityai:upgrade-intent:v1';
const MAX_PATH_LENGTH = 1200;

function clean(value, maxLength = 160) {
  return typeof value === 'string' ? value.trim().slice(0, maxLength) : '';
}

export function validateUpgradeReturnPath(value, companyId) {
  const path = clean(value, MAX_PATH_LENGTH);
  const tenant = clean(companyId, 180);
  if (!path || !tenant || !path.startsWith('/') || path.startsWith('//')) return '';
  try {
    const url = new URL(path, 'https://affinityai.invalid');
    if (url.origin !== 'https://affinityai.invalid') return '';
    const prefix = `/instance/${encodeURIComponent(tenant)}/`;
    if (!url.pathname.startsWith(prefix)) return '';
    if (url.pathname.includes('/../') || url.pathname.includes('/auth')) return '';
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return '';
  }
}

export function normalizeUpgradeIntent(value) {
  if (!value || typeof value !== 'object') return null;
  const companyId = clean(value.companyId, 180);
  const reason = clean(value.reason, 80);
  const returnAction = clean(value.returnAction, 80);
  const returnPath = validateUpgradeReturnPath(value.returnPath, companyId);
  if (!companyId || !isUpgradeReason(reason) || !isUpgradeReturnAction(returnAction) || !returnPath) {
    return null;
  }
  return {
    version: 1,
    companyId,
    reason,
    returnAction,
    returnPath,
    createdAt: Number.isFinite(Number(value.createdAt)) ? Number(value.createdAt) : Date.now(),
  };
}

export function createUpgradeIntent({companyId, reason, returnAction, returnPath}) {
  const prompt = getConversionPrompt(reason);
  return normalizeUpgradeIntent({
    companyId,
    reason,
    returnAction: returnAction || prompt.returnAction,
    returnPath,
    createdAt: Date.now(),
  });
}

export function persistUpgradeIntent(value, storage = globalThis.sessionStorage) {
  const intent = normalizeUpgradeIntent(value);
  if (!intent || !storage) return null;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(intent));
    return intent;
  } catch {
    return null;
  }
}

export function readUpgradeIntent(storage = globalThis.sessionStorage) {
  if (!storage) return null;
  try {
    return normalizeUpgradeIntent(JSON.parse(storage.getItem(STORAGE_KEY) || 'null'));
  } catch {
    return null;
  }
}

export function clearUpgradeIntent(storage = globalThis.sessionStorage) {
  try {
    storage?.removeItem(STORAGE_KEY);
  } catch {
    // This state improves navigation only and must never affect entitlements.
  }
}

export function buildContextualPlansPath(companyId, reason) {
  const safeReason = isUpgradeReason(reason) ? reason : 'free_status';
  return `/instance/${encodeURIComponent(companyId)}/dashboard?settings=plan&upgrade=${encodeURIComponent(
    safeReason,
  )}`;
}

export function addUpgradeReturnState(path, state, reason = '') {
  try {
    const url = new URL(path, 'https://affinityai.invalid');
    url.searchParams.set('upgradeReturn', state === 'activated' ? 'activated' : 'cancelled');
    if (isUpgradeReason(reason)) url.searchParams.set('upgradeReason', reason);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return path;
  }
}

export function claimOneTimeCompanyHalo({companyId, userId, storage = globalThis.localStorage}) {
  const tenant = clean(companyId, 180);
  const user = clean(userId, 180);
  if (!tenant || !user || !storage) return false;
  const key = `affinityai:new-scenario-halo:v1:${user}:${tenant}`;
  try {
    if (storage.getItem(key) === 'shown') return false;
    storage.setItem(key, 'shown');
    return true;
  } catch {
    return false;
  }
}
